import { useState } from "react";
import { useTranslation } from "@localization/useTranslation";
import { isNullOrWhitespace } from "@common/stringOperations";
import { useDotLegalQuery } from "@common/hooks/dotLegalReactQuery";
import { useSecurityMeasuresQuery } from "@common/hooks/useSelectableItemQueries";
import { SelectableItem } from "../../processingActivity/ProcessingActivity.types";
import { RiskAreaViewModel } from "../../riskSettings/riskArea/riskAreas/RiskAreas.types";
import { getRiskLevelIndicatorChipStyle } from "../../riskSettings/riskArea/riskAreaDialog/riskLevels/riskLevelIndicators";
import { toScoreOption } from "../riskAssessmentsOverview/assessDialog/AssessDialog.utils";
import { useRiskExposureConfig } from "../riskExposure/useRiskExposureConfig";
import { RiskExposureMode } from "../riskExposure/RiskExposure.types";
import { AssessmentConfidence, getAssessmentConfidenceOptions } from "../AssessmentConfidence";
import { useResponsibleQuery } from "../../user/hooks/useResponsibleQuery";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider";

const emptyErrors = {
    consequence: "",
    probability: "",
    consequenceJustification: "",
    probabilityJustification: "",
};

// The shared consequence/probability scoring state used by every place that scores a risk (the single-entity
// assess dialog and the operational bulk-assess dialog). Owns the form state, the master-data option queries,
// the calculated-risk lookup, the matrix-cell handler and the required-field validation — so neither caller
// re-implements any of it. The form always starts empty; callers seed it only through the exposed setters.
export function useRiskScoringForm(riskAreaId: string) {
    const { translateString } = useTranslation();

    const riskAreasQuery = useDotLegalQuery<Array<RiskAreaViewModel>>("/risk-areas");
    const consequenceTypesQuery = useDotLegalQuery<Array<SelectableItem>>(`/risk-consequences/selectable?riskAreaId=${riskAreaId}`);
    const measuresQuery = useSecurityMeasuresQuery(true, false);
    const responsibleQuery = useResponsibleQuery();
    const { userProfileId } = useUserContext();

    const [consequence, setConsequenceState] = useState<number | null>(null);
    const [probability, setProbabilityState] = useState<number | null>(null);
    const [consequenceJustification, setConsequenceJustificationState] = useState("");
    const [probabilityJustification, setProbabilityJustificationState] = useState("");
    const [selectedConsequenceTypeIds, setSelectedConsequenceTypeIds] = useState<Array<string>>([]);
    const [selectedMeasureIds, setSelectedMeasureIds] = useState<Array<string>>([]);
    const [selectedParticipantIds, setSelectedParticipantIds] = useState<Array<string>>([]);
    const [captureExposure, setCaptureExposure] = useState(false);
    const [exposureValueText, setExposureValueText] = useState("");
    const [confidence, setConfidence] = useState<AssessmentConfidence | null>(null);
    const [errors, setErrors] = useState(emptyErrors);

    const { exposureMode, exposureEnabled, exposureCurrencyCode, exposureScaleUpperBound } = useRiskExposureConfig();

    const exposureScaleOptions = Array.from({ length: exposureScaleUpperBound ?? 0 }, (_, index) => ({
        id: (index + 1).toString(),
        name: (index + 1).toString(),
    }));

    const parsedExposure = exposureValueText === "" ? null : Number(exposureValueText);
    const exposureValue = captureExposure && parsedExposure != null && !isNaN(parsedExposure) ? parsedExposure : null;
    const exposureValid =
        !captureExposure ||
        exposureValue == null ||
        exposureMode !== RiskExposureMode.Scale ||
        (Number.isInteger(exposureValue) && exposureValue >= 1 && exposureValue <= (exposureScaleUpperBound ?? 0));

    const riskArea = riskAreasQuery.data?.find((area) => area.id === riskAreaId);
    const consequenceOptions = riskArea ? toScoreOption(riskArea.consequenceLevels) : [];
    const probabilityOptions = riskArea ? toScoreOption(riskArea.probabilityLevels) : [];
    const consequenceTypeOptions = consequenceTypesQuery.data ?? [];
    const measureOptions = measuresQuery.data ?? [];
    // The assessor is recorded as assessed-by, never as a participant.
    const participantOptions = (responsibleQuery.dataForMultiSelect(selectedParticipantIds) ?? []).filter((user) => user.id !== userProfileId);
    const confidenceOptions = getAssessmentConfidenceOptions(translateString);

    const isLoading = riskAreasQuery.isLoading || consequenceTypesQuery.isLoading || measuresQuery.isLoading || responsibleQuery.isLoading;

    // Pure validity (no error side effects) so callers can disable a save button while the form is incomplete.
    const isValid =
        consequence != null &&
        probability != null &&
        !isNullOrWhitespace(consequenceJustification) &&
        !isNullOrWhitespace(probabilityJustification) &&
        exposureValid;

    const calculatedRisk = riskArea && consequence != null && probability != null ? riskArea.matrix[consequence - 1][probability - 1] : null;
    const calculatedRiskLabel = riskArea && calculatedRisk != null ? riskArea.riskLevels[calculatedRisk - 1] : "";
    const calculatedRiskStyle = riskArea && calculatedRisk != null ? getRiskLevelIndicatorChipStyle(calculatedRisk, riskArea.riskLevelIndicators) : null;

    const setConsequence = (value: number | null) => {
        setConsequenceState(value);
        setErrors((current) => ({ ...current, consequence: value == null ? translateString("fieldRequired") : "" }));
    };

    const setProbability = (value: number | null) => {
        setProbabilityState(value);
        setErrors((current) => ({ ...current, probability: value == null ? translateString("fieldRequired") : "" }));
    };

    const setConsequenceJustification = (value: string) => {
        setConsequenceJustificationState(value);
        setErrors((current) => ({ ...current, consequenceJustification: isNullOrWhitespace(value) ? translateString("fieldRequired") : "" }));
    };

    const setProbabilityJustification = (value: string) => {
        setProbabilityJustificationState(value);
        setErrors((current) => ({ ...current, probabilityJustification: isNullOrWhitespace(value) ? translateString("fieldRequired") : "" }));
    };

    const onMatrixCellClick = (clickedConsequence: number, clickedProbability: number) => {
        setConsequence(clickedConsequence);
        setProbability(clickedProbability);
    };

    const validate = () => {
        const validationErrors = {
            consequence: consequence == null ? translateString("fieldRequired") : "",
            probability: probability == null ? translateString("fieldRequired") : "",
            consequenceJustification: isNullOrWhitespace(consequenceJustification) ? translateString("fieldRequired") : "",
            probabilityJustification: isNullOrWhitespace(probabilityJustification) ? translateString("fieldRequired") : "",
        };
        setErrors(validationErrors);
        return Object.values(validationErrors).every((error) => error === "");
    };

    const reset = () => {
        setConsequenceState(null);
        setProbabilityState(null);
        setConsequenceJustificationState("");
        setProbabilityJustificationState("");
        setSelectedConsequenceTypeIds([]);
        setSelectedMeasureIds([]);
        setSelectedParticipantIds([]);
        setCaptureExposure(false);
        setExposureValueText("");
        setConfidence(null);
        setErrors(emptyErrors);
    };

    return {
        isLoading,
        isValid,
        riskArea,
        consequence,
        setConsequence,
        probability,
        setProbability,
        consequenceJustification,
        setConsequenceJustification,
        probabilityJustification,
        setProbabilityJustification,
        consequenceOptions,
        probabilityOptions,
        consequenceTypeOptions,
        measureOptions,
        selectedConsequenceTypeIds,
        setSelectedConsequenceTypeIds,
        selectedMeasureIds,
        setSelectedMeasureIds,
        participantOptions,
        selectedParticipantIds,
        setSelectedParticipantIds,
        calculatedRisk,
        calculatedRiskLabel,
        calculatedRiskStyle,
        onMatrixCellClick,
        exposureMode,
        exposureEnabled,
        exposureCurrencyCode,
        exposureScaleUpperBound,
        exposureScaleOptions,
        captureExposure,
        setCaptureExposure,
        exposureValueText,
        setExposureValueText,
        exposureValue,
        confidence,
        setConfidence,
        confidenceOptions,
        errors,
        validate,
        reset,
    };
}

export type RiskScoringForm = ReturnType<typeof useRiskScoringForm>;
